import React, { useState, useContext } from "react";
import { useResponsive } from "../components/responsive";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Users, Plus, X, Award, ChevronDown, ChevronUp, User, Search, Sparkles, CheckCircle, AlertCircle, Zap, Trash2, Edit, Eye, Mail, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useForm } from "react-hook-form";
import AuthContext from "../context/AuthContext";
import { getAvatarStyle } from "../utils/avatarColors";
import UserAvatar from "../components/UserAvatar";
import teamsService from "../services/teamsService";
import organizationsService from "../services/organizationsService";
import api from "../services/api";
import useCustomToast from "../hooks/useCustomToast";
import { formatError } from "../utils/errorHandler";
import LanguageSelect from "../components/LanguageSelect";
import { useTranslation } from "../i18n";

const TeamsPage = () => {
  const { user, isInitialized } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const toast = useCustomToast();
  const { isMobile } = useResponsive();
  const { t } = useTranslation("teams");

  const [isFormOpen, setFormOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("grid"); // grid or list — forced to 'list' on mobile
  const effectiveViewMode = isMobile ? "list" : viewMode;
  const [selectedTeamForAdmin, setSelectedTeamForAdmin] = useState(null);
  const [selectedTeamForEdit, setSelectedTeamForEdit] = useState(null);
  const [selectedTeamForDelete, setSelectedTeamForDelete] = useState(null);
  const [selectedTeamForMembers, setSelectedTeamForMembers] = useState(null);
  const [showBatchGeneration, setShowBatchGeneration] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  // Batch generation state
  const [batchConfig, setBatchConfig] = useState({
    organization_id: "",
    fields: [],
    operator: "AND",
    include_missing_values: false,
  });
  const [batchPreview, setBatchPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const isAdmin = user?.is_superuser || user?.role === 'admin';
  const deleteConfirmParts = t("delete.confirm").split("{{name}}");

  // Fetch teams - wait for user to load, refetch every time page mounts
  const { data: rawTeams = [], isLoading: teamsLoading } = useQuery({
    queryKey: ["teams", user?.id, isAdmin],
    queryFn: () => user?.is_superuser ? teamsService.getAllTeams() : teamsService.getMyTeams(),
    enabled: !!user,
    staleTime: 0,
    refetchOnMount: "always",
  });

  // Backend already filters inactive for non-admins, but double-check on frontend
  const teams = isAdmin ? rawTeams : rawTeams.filter(t => t.is_active !== false);

  // Fetch organizations (for batch generation and team creation)
  const { data: organizations = [] } = useQuery({
    queryKey: ["organizations"],
    queryFn: () => organizationsService.list(),
    enabled: user?.is_superuser || false,
  });

  // Fetch users (for team admin and member assignment)
  const { data: allUsers = [] } = useQuery({
    queryKey: ["allUsers", isAdmin],
    queryFn: async () => {
      // Page through the org directory so large orgs (>100 users) load fully;
      // the endpoint caps each page at limit=100 and returns the total count.
      const apiBase = process.env.REACT_APP_API_URL || '';
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
      const limit = 100;
      let skip = 0;
      let fetched = [];
      let totalCount = 0;
      do {
        const response = await fetch(`${apiBase}/api/v1/users/?skip=${skip}&limit=${limit}`, { headers });
        const data = await response.json();
        const page = data.data || [];
        fetched = [...fetched, ...page];
        totalCount = data.count ?? page.length;
        skip += limit;
        if (page.length === 0) break;
      } while (fetched.length < totalCount);
      return fetched;
    },
    enabled: !!user && isAdmin,
  });

  const createTeamMutation = useMutation({
    mutationFn: (teamData) => teamsService.createTeam(teamData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      toast.success(t("toasts.team_created"));
      setFormOpen(false);
    },
    onError: (error) => {
      toast.error(formatError(error));
    },
  });

  // Update team mutation
  const updateTeamMutation = useMutation({
    mutationFn: ({ teamId, teamData }) => teamsService.updateTeam(teamId, teamData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      toast.success(t("toasts.team_updated"));
      setSelectedTeamForEdit(null);
    },
    onError: (error) => {
      toast.error(formatError(error));
    },
  });

  // Delete team mutation
  const deleteTeamMutation = useMutation({
    mutationFn: (teamId) => teamsService.deleteTeam(teamId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      toast.success(t("toasts.team_deleted"));
      setSelectedTeamForDelete(null);
    },
    onError: (error) => {
      toast.error(formatError(error));
    },
  });

  // Batch generate teams mutation
  const batchGenerateMutation = useMutation({
    mutationFn: (config) => teamsService.batchCreateTeams({ ...config, dry_run: false }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      toast.success(t("toasts.batch_created", { teams: data.created_team_ids?.length || 0, memberships: data.memberships_created || 0 }));
      setShowPreview(false);
      setBatchPreview(null);
      setShowBatchGeneration(false);
      setBatchConfig({
        organization_id: "",
        fields: [],
        operator: "AND",
        include_missing_values: false,
      });
    },
    onError: (error) => {
      toast.error(formatError(error));
    },
  });

  // Preview batch generation
  const previewBatchMutation = useMutation({
    mutationFn: (config) => teamsService.batchCreateTeams({ ...config, dry_run: true }),
    onSuccess: (data) => {
      setBatchPreview(data);
      setShowPreview(true);
    },
    onError: (error) => {
      toast.error(formatError(error));
    },
  });

  // Assign team admin mutation
  const assignTeamAdminMutation = useMutation({
    mutationFn: ({ teamId, userId }) => teamsService.assignTeamAdmin(teamId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      toast.success(t("toasts.admin_assigned"));
      setSelectedTeamForAdmin(null);
    },
    onError: (error) => {
      toast.error(formatError(error));
    },
  });

  // Form for creating teams
  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      name: "",
      description: "",
      organization_id: "",
    },
  });

  const handleCreateTeam = (data) => {
    createTeamMutation.mutate({
      name: data.name,
      description: data.description || null,
    });
  };

  // Handle field checkbox toggle for batch generation
  const handleFieldToggle = (field) => {
    setBatchConfig(prev => {
      const newFields = prev.fields.includes(field)
        ? prev.fields.filter(f => f !== field)
        : [...prev.fields, field];
      return { ...prev, fields: newFields };
    });
  };

  // Handle batch generation preview
  const handlePreviewBatch = () => {
    if (batchConfig.fields.length === 0) {
      toast.warning(t("toasts.select_field"));
      return;
    }

    if (!batchConfig.organization_id) {
      toast.warning(t("toasts.select_organization"));
      return;
    }

    previewBatchMutation.mutate(batchConfig);
  };

  // Handle batch generation execution
  const handleExecuteBatch = () => {
    batchGenerateMutation.mutate(batchConfig);
  };

  // Split teams into active and inactive, then filter by search
  // Only admins see inactive teams
  const searchFilter = (team) =>
    team.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    team.description?.toLowerCase().includes(searchQuery.toLowerCase());

  const filteredTeams = teams.filter(t => t.is_active !== false).filter(searchFilter);
  const inactiveTeams = isAdmin ? teams.filter(t => t.is_active === false).filter(searchFilter) : [];

  // Wait for auth context to finish loading before rendering
  if (!isInitialized || !user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row gap-6 pb-8 pt-3 sm:pt-8 px-4 sm:px-6 lg:px-8 bg-gray-50 min-h-screen w-full">
      {/* Main Content */}
      <div className="w-full lg:w-3/4 space-y-6">
        {/* Action buttons — hidden on mobile; Add Teams is folded into the search bar below */}
        <div className="hidden sm:flex justify-end gap-2 flex-wrap">
          {(user?.is_superuser) && (
            <button
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600
                         text-white font-medium px-4 py-2 rounded-full shadow-md transition-all flex items-center gap-2"
              onClick={() => setShowBatchGeneration(!showBatchGeneration)}
            >
              <Sparkles size={18} /> {t("actions.batch_generate")}
            </button>
          )}
          {isAdmin && (
            <button
              className="org-gradient transition-all
                         text-white font-medium px-4 py-2 rounded-full shadow-md flex items-center gap-2"
              onClick={() => setFormOpen(true)}
            >
              <Plus size={18} /> {t("actions.add_teams")}
            </button>
          )}
        </div>

        {/* Batch Generation Section (Admin Only) */}
        <AnimatePresence>
          {showBatchGeneration && user?.is_superuser && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-white rounded-2xl shadow-md overflow-hidden"
            >
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-white" />
                  <h3 className="font-bold text-white">{t("batch.title")}</h3>
                </div>
                <p className="text-sm text-white/90 mt-1">
                  {t("batch.subtitle")}
                </p>
              </div>

              <div className="p-6 space-y-4">
                {/* Organization Selector */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("batch.organization")}
                  </label>
                  <select
                    className="w-full border border-slate-300 rounded-full px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    value={batchConfig.organization_id}
                    onChange={(e) => setBatchConfig({ ...batchConfig, organization_id: e.target.value })}
                  >
                    <option value="">{t("batch.select_organization")}</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Field Grouping */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("batch.group_by")}
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={batchConfig.fields.includes("department")}
                        onChange={() => handleFieldToggle("department")}
                        className="w-4 h-4 text-amber-500 rounded focus:ring-amber-500"
                      />
                      <span className="text-sm text-slate-700">{t("batch.fields.department")}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={batchConfig.fields.includes("location")}
                        onChange={() => handleFieldToggle("location")}
                        className="w-4 h-4 text-amber-500 rounded focus:ring-amber-500"
                      />
                      <span className="text-sm text-slate-700">{t("batch.fields.location")}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={batchConfig.fields.includes("job_title")}
                        onChange={() => handleFieldToggle("job_title")}
                        className="w-4 h-4 text-amber-500 rounded focus:ring-amber-500"
                      />
                      <span className="text-sm text-slate-700">{t("batch.fields.job_title")}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={batchConfig.fields.includes("role")}
                        onChange={() => handleFieldToggle("role")}
                        className="w-4 h-4 text-amber-500 rounded focus:ring-amber-500"
                      />
                      <span className="text-sm text-slate-700">{t("batch.fields.role")}</span>
                    </label>
                  </div>
                </div>

                {/* Logic Operator (if multiple fields selected) */}
                {batchConfig.fields.length > 1 && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("batch.grouping_logic")}
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="logicOperator"
                          value="AND"
                          checked={batchConfig.operator === "AND"}
                          onChange={(e) => setBatchConfig({ ...batchConfig, operator: e.target.value })}
                          className="w-4 h-4 text-amber-500 focus:ring-amber-500"
                        />
                        <span className="text-sm text-slate-700">{t("batch.operator_and")}</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="logicOperator"
                          value="OR"
                          checked={batchConfig.operator === "OR"}
                          onChange={(e) => setBatchConfig({ ...batchConfig, operator: e.target.value })}
                          className="w-4 h-4 text-amber-500 focus:ring-amber-500"
                        />
                        <span className="text-sm text-slate-700">{t("batch.operator_or")}</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Include Empty Values */}
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={batchConfig.include_missing_values}
                      onChange={(e) => setBatchConfig({ ...batchConfig, include_missing_values: e.target.checked })}
                      className="w-4 h-4 text-amber-500 rounded focus:ring-amber-500"
                    />
                    <span className="text-sm text-slate-700">{t("batch.include_missing")}</span>
                  </label>
                  <p className="text-xs text-slate-500 ml-6 mt-1">
                    {t("batch.include_missing_help")}
                  </p>
                </div>

                {/* Preview Button */}
                <div className="flex gap-2 pt-4">
                  <button
                    onClick={handlePreviewBatch}
                    disabled={previewBatchMutation.isPending}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full hover:from-amber-600 hover:to-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {previewBatchMutation.isPending ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {t("batch.loading_preview")}
                      </>
                    ) : (
                      <>
                        <Search size={18} />
                        {t("batch.preview_teams")}
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setShowBatchGeneration(false);
                      setShowPreview(false);
                      setBatchPreview(null);
                    }}
                    className="px-4 py-2 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors"
                  >
                    {t("common:actions.cancel")}
                  </button>
                </div>

                {/* Preview Results */}
                {showPreview && batchPreview && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4 p-4 bg-slate-50 rounded-2xl border border-slate-200"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-slate-800">{t("batch.preview_results")}</h4>
                      <div className="text-sm text-slate-600">
                        {t("batch.teams_to_create", { count: batchPreview.group_count || 0 })}
                        {batchPreview.skipped_users > 0 && (
                          <span className="ml-2 text-amber-600">{t("batch.users_skipped", { count: batchPreview.skipped_users })}</span>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {batchPreview.previews?.map((preview, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-white rounded-2xl border border-slate-200">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${preview.existing_team_id ? 'bg-blue-100' : 'bg-green-100'}`}>
                              {preview.existing_team_id ? (
                                <CheckCircle className="w-5 h-5 text-blue-600" />
                              ) : (
                                <Sparkles className="w-5 h-5 text-green-600" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-slate-800">{preview.name}</p>
                              <p className="text-xs text-slate-500">
                                {preview.existing_team_id ? t("batch.existing_team") : t("batch.new_team")}
                              </p>
                            </div>
                          </div>
                          <div className="text-sm text-slate-600 bg-slate-100 px-3 py-1 rounded-full">
                            {t("members_count", { count: preview.member_count || 0 })}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Execute Button */}
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <button
                        onClick={handleExecuteBatch}
                        disabled={batchGenerateMutation.isPending}
                        className="w-full px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-full hover:from-green-600 hover:to-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
                      >
                        {batchGenerateMutation.isPending ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            {t("batch.creating_teams")}
                          </>
                        ) : (
                          <>
                            <Zap size={18} />
                            {t("batch.create_teams")}
                          </>
                        )}
                      </button>
                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Search and Filter Bar */}
        <div className="bg-white rounded-2xl shadow-md p-3 flex flex-col sm:flex-row gap-3 items-center">
          {/* Search input (with inline Add button on mobile) */}
          <div className="flex items-center gap-2 flex-1 w-full">
            <div className="relative flex-1 w-full">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={18} className="text-slate-400" />
              </div>
              <input
                type="text"
                placeholder={t("search.placeholder")}
                className="pl-10 pr-4 py-2 w-full border border-slate-200 rounded-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-slate-600"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            {isAdmin && (
              <button
                className="sm:hidden org-gradient text-white font-medium px-3 py-2 rounded-full shadow-sm flex items-center gap-1 text-sm flex-shrink-0"
                onClick={() => setFormOpen(true)}
                aria-label={t("actions.add_teams_aria")}
              >
                <Plus size={16} /> {t("common:actions.add")}
              </button>
            )}
          </div>

          {/* Grid/List toggle — desktop only; mobile is always list */}
          <div className="hidden sm:flex items-center border border-slate-200 rounded-2xl overflow-hidden">
            <button
              className={`px-3 py-2 ${viewMode === 'grid' ? 'bg-blue-50 text-blue-600' : 'text-slate-500 hover:bg-slate-50'}`}
              onClick={() => setViewMode('grid')}
            >
              <div className="flex items-center gap-1">
                <div className="grid grid-cols-2 gap-0.5">
                  <div className="w-1.5 h-1.5 bg-current rounded-sm"></div>
                  <div className="w-1.5 h-1.5 bg-current rounded-sm"></div>
                  <div className="w-1.5 h-1.5 bg-current rounded-sm"></div>
                  <div className="w-1.5 h-1.5 bg-current rounded-sm"></div>
                </div>
                <span className="text-sm">{t("search.grid")}</span>
              </div>
            </button>
            <button
              className={`px-3 py-2 ${viewMode === 'list' ? 'bg-blue-50 text-blue-600' : 'text-slate-500 hover:bg-slate-50'}`}
              onClick={() => setViewMode('list')}
            >
              <div className="flex items-center gap-1">
                <div className="flex flex-col gap-0.5">
                  <div className="w-4 h-1 bg-current rounded-sm"></div>
                  <div className="w-4 h-1 bg-current rounded-sm"></div>
                  <div className="w-4 h-1 bg-current rounded-sm"></div>
                </div>
                <span className="text-sm">{t("search.list")}</span>
              </div>
            </button>
          </div>
        </div>

        {/* Teams Display */}
        {teamsLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
          </div>
        ) : filteredTeams.length > 0 ? (
          effectiveViewMode === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {filteredTeams.map((team) => (
                <TeamCard
                  key={team.id}
                  team={team}
                  onManageAdmin={() => setSelectedTeamForAdmin(team)}
                  onEdit={() => setSelectedTeamForEdit(team)}
                  onDelete={() => setSelectedTeamForDelete(team)}
                  onViewMembers={() => setSelectedTeamForMembers(team)}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredTeams.map((team) => (
                <TeamListItem
                  key={team.id}
                  team={team}
                  onManageAdmin={() => setSelectedTeamForAdmin(team)}
                  onEdit={() => setSelectedTeamForEdit(team)}
                  onDelete={() => setSelectedTeamForDelete(team)}
                  onViewMembers={() => setSelectedTeamForMembers(team)}
                />
              ))}
            </div>
          )
        ) : (
          <div className="bg-white rounded-2xl shadow-md p-8 text-center">
            <div className="flex flex-col items-center justify-center">
              <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                <Users size={32} className="text-slate-400" />
              </div>
              <h3 className="text-lg font-medium text-slate-700 mb-1">{t("empty.title")}</h3>
              <p className="text-slate-500 max-w-md">
                {searchQuery
                  ? t("empty.no_match")
                  : t("empty.get_started")}
              </p>
              {user?.is_superuser && (
                <button
                  className="mt-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 rounded-full shadow-sm hover:from-amber-600 hover:to-orange-600 transition-all flex items-center gap-2"
                  onClick={() => setShowBatchGeneration(true)}
                >
                  <Sparkles size={16} /> {t("actions.generate_teams")}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Inactive Teams Panel */}
        {isAdmin && inactiveTeams.length > 0 && (
          <div className="bg-white rounded-2xl shadow-md overflow-hidden">
            <button
              onClick={() => setShowInactive(!showInactive)}
              className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                  <Trash2 size={16} className="text-slate-400" />
                </div>
                <span className="font-medium text-slate-600">{t("inactive.title")}</span>
                <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{inactiveTeams.length}</span>
              </div>
              {showInactive ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
            </button>
            <AnimatePresence>
              {showInactive && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="px-4 pb-4 space-y-2">
                    {inactiveTeams.map((team) => (
                      <div key={team.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl opacity-60">
                        <div>
                          <span className="font-medium text-slate-700">{team.name}</span>
                          {team.description && <p className="text-xs text-slate-400">{team.description}</p>}
                        </div>
                        <button
                          onClick={() => {
                            updateTeamMutation.mutate({ teamId: team.id, teamData: { is_active: true } });
                          }}
                          className="text-xs px-3 py-1 bg-green-50 text-green-600 hover:bg-green-100 rounded-full transition-colors font-medium"
                        >
                          {t("actions.restore")}
                        </button>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Right sidebar */}
      <div className="w-full lg:w-1/4 space-y-6 sticky top-20 h-fit">
        <TeamStats teams={teams.filter(t => t.is_active !== false)} />
        <TopTeams teams={teams.filter(t => t.is_active !== false)} />
      </div>

      {/* Team Creation Modal - Shows info about using Batch Generate */}
      <AnimatePresence>
        {isFormOpen && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
            <motion.div
              className="bg-white rounded-2xl shadow-xl w-full max-w-md"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <div className="flex justify-between items-center p-6 border-b border-slate-200">
                <h2 className="text-xl font-bold text-slate-800">{t("create.title")}</h2>
                <button
                  onClick={() => {
                    setFormOpen(false);
                    reset();
                  }}
                  className="text-slate-500 hover:text-slate-700 p-1 rounded-full hover:bg-slate-100"
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleSubmit(handleCreateTeam)} className="p-6">
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t("create.name_label")}</label>
                    <input
                      {...register("name", { required: t("create.name_required") })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder={t("create.name_placeholder")}
                    />
                    {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t("create.description")}</label>
                    <textarea
                      {...register("description")}
                      className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      rows={3}
                      placeholder={t("create.description_placeholder")}
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="px-4 py-2 border border-slate-300 rounded-full text-slate-700 hover:bg-slate-50"
                    onClick={() => {
                      setFormOpen(false);
                      reset();
                    }}
                  >
                    {t("common:actions.cancel")}
                  </button>
                  <button
                    type="submit"
                    disabled={createTeamMutation.isPending}
                    className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-full shadow-sm hover:from-indigo-600 hover:to-purple-600 transition-all flex items-center gap-2 disabled:opacity-50"
                  >
                    <Plus size={16} />
                    {createTeamMutation.isPending ? t("create.creating") : t("create.submit")}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Team Edit Modal */}
      <AnimatePresence>
        {selectedTeamForEdit && (
          <TeamEditModal
            team={selectedTeamForEdit}
            onClose={() => setSelectedTeamForEdit(null)}
            onSave={(teamData) => updateTeamMutation.mutate({ teamId: selectedTeamForEdit.id, teamData })}
            isPending={updateTeamMutation.isPending}
          />
        )}
      </AnimatePresence>

      {/* Team Delete Confirmation Modal */}
      <AnimatePresence>
        {selectedTeamForDelete && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
            <motion.div
              className="bg-white rounded-2xl shadow-xl w-full max-w-md"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <Trash2 className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-800">{t("delete.title")}</h2>
                    <p className="text-sm text-slate-500">{t("delete.subtitle")}</p>
                  </div>
                </div>
                <p className="text-slate-600 mb-6">
                  {deleteConfirmParts[0]}<strong>{selectedTeamForDelete.name}</strong>{deleteConfirmParts[1]} {t("delete.memberships_note")}
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setSelectedTeamForDelete(null)}
                    className="px-4 py-2 border border-slate-300 rounded-full text-slate-700 hover:bg-slate-50"
                  >
                    {t("common:actions.cancel")}
                  </button>
                  <button
                    onClick={() => deleteTeamMutation.mutate(selectedTeamForDelete.id)}
                    disabled={deleteTeamMutation.isPending}
                    className="px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {deleteTeamMutation.isPending ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {t("delete.deleting")}
                      </>
                    ) : (
                      <>
                        <Trash2 size={16} />
                        {t("common:actions.delete")}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Team Admin Assignment Modal */}
      <AnimatePresence>
        {selectedTeamForAdmin && (
          <TeamAdminModal
            team={selectedTeamForAdmin}
            users={allUsers}
            onClose={() => setSelectedTeamForAdmin(null)}
            onAssign={(userId) => assignTeamAdminMutation.mutate({ teamId: selectedTeamForAdmin.id, userId })}
            isPending={assignTeamAdminMutation.isPending}
          />
        )}
      </AnimatePresence>

      {/* Team Members Modal */}
      <AnimatePresence>
        {selectedTeamForMembers && (
          <TeamMembersModal
            team={selectedTeamForMembers}
            onClose={() => setSelectedTeamForMembers(null)}
            allUsers={allUsers}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Color palette for teams
const TEAM_COLORS = [
  { from: "from-purple-500",  to: "to-blue-500",   border: "border-purple-300",  hex: "#a855f7" },
  { from: "from-emerald-500", to: "to-teal-500",   border: "border-emerald-300", hex: "#10b981" },
  { from: "from-rose-500",    to: "to-pink-500",   border: "border-rose-300",    hex: "#f43f5e" },
  { from: "from-amber-500",   to: "to-orange-500", border: "border-amber-300",   hex: "#f59e0b" },
  { from: "from-cyan-500",    to: "to-blue-500",   border: "border-cyan-300",    hex: "#06b6d4" },
  { from: "from-violet-500",  to: "to-purple-500", border: "border-violet-300",  hex: "#8b5cf6" },
  { from: "from-lime-500",    to: "to-green-500",  border: "border-lime-300",    hex: "#84cc16" },
  { from: "from-fuchsia-500", to: "to-pink-500",   border: "border-fuchsia-300", hex: "#d946ef" },
];

// Get consistent color for a team based on its name
const getTeamColor = (teamName) => {
  if (!teamName) return TEAM_COLORS[0];
  let hash = 0;
  for (let i = 0; i < teamName.length; i++) {
    hash = teamName.charCodeAt(i) + ((hash << 5) - hash);
  }
  return TEAM_COLORS[Math.abs(hash) % TEAM_COLORS.length];
};

const TeamCard = ({ team, onManageAdmin, onEdit, onDelete, onViewMembers }) => {
  const { t } = useTranslation("teams");
  const { user } = useContext(AuthContext);
  const isAdmin = user?.is_superuser || user?.role === 'admin';
  const teamColor = getTeamColor(team.name);

  return (
    <motion.div
      className="bg-white rounded-2xl overflow-hidden border-2 border-slate-200 relative group cursor-pointer
                 transition-all duration-300 ease-out
                 [box-shadow:0_1px_3px_0_rgba(0,0,0,0.1)]
                 hover:-translate-y-1
                 hover:[border-color:var(--team-hex)]
                 hover:[box-shadow:0_20px_40px_-12px_var(--team-shadow),0_8px_16px_-8px_rgba(0,0,0,0.1)]"
      style={{
        '--team-hex': teamColor.hex,
        '--team-shadow': `${teamColor.hex}55`,
      }}
      onClick={() => onViewMembers()}
    >
      <div className="p-3 sm:px-4 sm:pt-10 sm:pb-5 flex sm:flex-col items-center sm:text-center gap-3 sm:gap-0">
        {/* Team Avatar */}
        <div
          className={`w-12 h-12 sm:w-20 sm:h-20 rounded-full flex items-center justify-center text-white shadow-sm sm:mb-3 flex-shrink-0 bg-gradient-to-br ${teamColor.from} ${teamColor.to}`}
        >
          <Users className="w-5 h-5 sm:w-7 sm:h-7" />
        </div>

        <div className="min-w-0 flex-1 sm:flex-none sm:w-full text-left sm:text-center">
          {/* Team Header */}
          <h3 className="font-bold text-slate-800 text-sm sm:text-md sm:mb-1 truncate sm:whitespace-normal">{team.name}</h3>

          {/* Brief Description — hidden on mobile to keep the card compact */}
          <p className="hidden sm:block text-xs text-slate-600 mb-3 line-clamp-2">{team.description || t("card.no_description")}</p>

          {/* Manager */}
          {team.manager_name && (
            <p className="hidden sm:block text-xs text-slate-500 mb-2">
              {t("card.manager_label")} <span className="font-medium">{team.manager_name}</span>
            </p>
          )}

          {/* Stats Row */}
          <div className="flex items-center justify-start sm:justify-center space-x-4 text-xs text-slate-500 sm:w-full">
            <div className="flex items-center">
              <User size={12} className="text-blue-500 mr-1" />
              <span>{t("members_count", { count: team.member_count || 0 })}</span>
            </div>
          </div>

          {/* View Members Button — desktop only; whole card is tappable on mobile */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onViewMembers();
            }}
            className="hidden sm:flex mt-3 items-center gap-1 text-xs text-slate-600 hover:text-slate-800 font-medium mx-auto"
          >
            <Eye size={14} />
            {t("card.view_members")}
          </button>
        </div>
      </div>

      {/* Quick action buttons */}
      {isAdmin && (
        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onManageAdmin();
            }}
            className="p-1.5 text-slate-400 hover:text-purple-500 hover:bg-slate-50 rounded-full transition-colors"
            title={t("card.manage_admin")}
          >
            <User size={16} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className="p-1.5 text-slate-400 hover:text-blue-500 hover:bg-slate-50 rounded-full transition-colors"
            title={t("card.edit_team")}
          >
            <Edit size={16} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-slate-50 rounded-full transition-colors"
            title={t("card.delete_team")}
          >
            <Trash2 size={16} />
          </button>
        </div>
      )}

      {/* Color strip at bottom - positioned absolutely */}
      <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${teamColor.from} ${teamColor.to}`}></div>
    </motion.div>
  );
};

const TeamListItem = ({ team, onManageAdmin, onEdit, onDelete, onViewMembers }) => {
  const { t } = useTranslation("teams");
  const { user } = useContext(AuthContext);
  const isAdmin = user?.is_superuser || user?.role === 'admin';
  const teamColor = getTeamColor(team.name);

  return (
    <motion.div
      className="bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-md transition-all border border-slate-200 relative group cursor-pointer"
      whileHover={{ x: 2 }}
      transition={{ type: "spring", stiffness: 400, damping: 17 }}
      onClick={() => onViewMembers()}
    >
      <div className="flex items-center p-3 gap-3">
        {/* Left color bar — desktop only */}
        <div className={`hidden sm:block w-1 h-12 rounded-full bg-gradient-to-b ${teamColor.from} ${teamColor.to}`}></div>

        {/* Team icon */}
        <div className={`w-11 h-11 sm:w-14 sm:h-14 rounded-full flex items-center justify-center text-white flex-shrink-0 shadow-sm bg-gradient-to-br ${teamColor.from} ${teamColor.to}`}>
          <Users className="w-5 h-5 sm:w-6 sm:h-6" />
        </div>

        {/* Team info */}
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-slate-800 text-sm sm:text-md truncate">{team.name}</h3>
          <p className="hidden sm:block text-xs text-slate-600 line-clamp-2 mb-1">{team.description || t("card.no_description")}</p>

          {/* Stats */}
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="flex items-center">
              <User size={12} className="text-blue-500 mr-1" />
              <span className="text-xs text-slate-700">{t("members_count", { count: team.member_count || 0 })}</span>
            </div>
            {team.manager_name && (
              <span className="hidden sm:inline text-xs text-slate-500">{t("card.manager", { name: team.manager_name })}</span>
            )}
          </div>
        </div>

        {/* View Members Button — desktop only; whole card is tappable on mobile */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onViewMembers();
          }}
          className="hidden sm:flex mr-2 items-center gap-1 px-3 py-1.5 text-xs text-slate-600 hover:text-slate-800 hover:bg-slate-100 font-medium rounded-full transition-colors"
        >
          <Eye size={14} />
          {t("card.view")}
        </button>

        {/* Action buttons — hidden on mobile to keep row compact */}
        {isAdmin && (
          <div className="hidden sm:flex gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onManageAdmin();
              }}
              className="p-2 text-slate-400 hover:text-purple-500 hover:bg-slate-50 rounded-full transition-colors"
              title={t("card.manage_admin")}
            >
              <User size={16} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className="p-2 text-slate-400 hover:text-blue-500 hover:bg-slate-50 rounded-full transition-colors"
              title={t("card.edit_team")}
            >
              <Edit size={16} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="p-2 text-slate-400 hover:text-red-500 hover:bg-slate-50 rounded-full transition-colors"
              title={t("card.delete_team")}
            >
              <Trash2 size={16} />
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

const TeamEditModal = ({ team, onClose, onSave, isPending }) => {
  const { t } = useTranslation("teams");
  const [name, setName] = useState(team.name);
  const [description, setDescription] = useState(team.description || "");
  const [isActive, setIsActive] = useState(team.is_active !== false);
  // "" = inherit the organization default
  const [defaultLocale, setDefaultLocale] = useState(team.default_locale || "");

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      name,
      description,
      is_active: isActive,
      default_locale: defaultLocale || null,
    });
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
      <motion.div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        <div className="flex justify-between items-center p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-800">{t("edit.title")}</h2>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 p-1 rounded-full hover:bg-slate-100"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t("edit.name_label")} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full border border-slate-300 p-2 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{t("edit.description")}</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full border border-slate-300 p-2 rounded-2xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t("edit.default_language")}
            </label>
            <LanguageSelect
              value={defaultLocale}
              onChange={setDefaultLocale}
              inheritLabel={t("edit.inherit_org_default")}
            />
            <p className="text-xs text-slate-500 mt-1">
              {t("edit.default_language_help")}
            </p>
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="w-4 h-4 text-purple-500 rounded focus:ring-purple-500"
              />
              <span className="text-sm text-slate-700">{t("edit.is_active")}</span>
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-slate-300 rounded-full text-slate-700 hover:bg-slate-50"
            >
              {t("common:actions.cancel")}
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="px-4 py-2 org-gradient text-white rounded-full shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isPending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {t("common:actions.saving")}
                </>
              ) : (
                t("common:actions.save_changes")
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

const TeamAdminModal = ({ team, users, onClose, onAssign, isPending }) => {
  const { t } = useTranslation("teams");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = React.useRef(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter users based on search query
  const filteredUsers = React.useMemo(() => {
    if (!searchQuery.trim()) return users.slice(0, 50); // Show first 50 by default
    const query = searchQuery.toLowerCase();
    return users.filter(
      (u) =>
        u.full_name?.toLowerCase().includes(query) ||
        u.email?.toLowerCase().includes(query)
    ).slice(0, 50); // Limit results
  }, [users, searchQuery]);

  // Get selected user for display
  const selectedUser = users.find((u) => u.id === selectedUserId);

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
      <motion.div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        <div className="flex justify-between items-center p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-800">{t("admin.title")}</h2>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 p-1 rounded-full hover:bg-slate-100"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-4">
            <p className="text-sm text-slate-600">
              {t("admin.team_label")} <span className="font-medium text-slate-800">{team.name}</span>
            </p>
            {team.manager_name && (
              <p className="text-sm text-slate-600 mt-1">
                {t("admin.current_manager_label")} <span className="font-medium text-slate-800">{team.manager_name}</span>
              </p>
            )}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {t("admin.select_user")}
            </label>
            <div className="relative" ref={dropdownRef}>
              {/* Selected value or search input */}
              <div
                className={`w-full px-3 py-2 border rounded-2xl flex items-center justify-between cursor-pointer ${
                  showDropdown ? "border-purple-500 ring-2 ring-purple-500" : "border-slate-300"
                }`}
                onClick={() => !isPending && setShowDropdown(true)}
              >
                {showDropdown ? (
                  <div className="flex items-center gap-2 w-full">
                    <Search className="w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={t("admin.search_placeholder")}
                      className="flex-1 outline-none bg-transparent"
                      autoFocus
                      disabled={isPending}
                    />
                  </div>
                ) : (
                  <>
                    <span className={selectedUser ? "text-slate-900" : "text-slate-500"}>
                      {selectedUser ? (selectedUser.full_name || selectedUser.email) : t("admin.search_prompt")}
                    </span>
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  </>
                )}
                {selectedUser && !showDropdown && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedUserId("");
                    }}
                    className="ml-2 p-1 hover:bg-slate-100 rounded"
                  >
                    <X className="w-4 h-4 text-slate-400" />
                  </button>
                )}
              </div>

              {/* Dropdown list */}
              <AnimatePresence>
                {showDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-2xl shadow-lg max-h-60 overflow-y-auto"
                  >
                    {filteredUsers.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-slate-500">
                        {searchQuery
                          ? t("admin.no_match")
                          : t("admin.no_users")}
                      </div>
                    ) : (
                      filteredUsers.map((u) => (
                        <div
                          key={u.id}
                          className={`px-4 py-3 cursor-pointer hover:bg-purple-50 transition-colors flex items-center gap-3 ${
                            selectedUserId === u.id ? "bg-purple-100" : ""
                          }`}
                          onClick={() => {
                            setSelectedUserId(u.id);
                            setSearchQuery("");
                            setShowDropdown(false);
                          }}
                        >
                          <UserAvatar
                            src={u.avatar_url}
                            name={u.full_name || u.email}
                            className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                            textClassName="text-xs"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-slate-900 truncate">{u.full_name || t("admin.no_name")}</div>
                            <div className="text-sm text-slate-500 truncate">{u.email}</div>
                          </div>
                        </div>
                      ))
                    )}
                    {filteredUsers.length > 0 && users.length > 50 && !searchQuery && (
                      <div className="px-4 py-2 text-xs text-slate-400 border-t border-slate-100">
                        {t("admin.type_to_search", { count: users.length })}
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-slate-300 rounded-full text-slate-700 hover:bg-slate-50"
            >
              {t("common:actions.cancel")}
            </button>
            <button
              onClick={() => selectedUserId && onAssign(selectedUserId)}
              disabled={!selectedUserId || isPending}
              className="px-4 py-2 org-gradient text-white rounded-full shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isPending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {t("admin.assigning")}
                </>
              ) : (
                t("admin.submit")
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const TeamMembersModal = ({ team, onClose, allUsers = [] }) => {
  const { t } = useTranslation("teams");
  const { user } = useContext(AuthContext);
  const isAdmin = user?.is_superuser || user?.role === 'admin';
  const toast = useCustomToast();
  const queryClient = useQueryClient();
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddMember, setShowAddMember] = useState(false);
  const [addSearchQuery, setAddSearchQuery] = useState("");
  const [selectedRole, setSelectedRole] = useState("member");

  const fetchMembers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get(`/api/v1/teams/${team.id}/members`);
      setMembers(response.data || []);
    } catch (err) {
      console.error("Failed to fetch team members:", err);
      setError(t("members.load_failed"));
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    fetchMembers();
  }, [team.id]);

  const memberUserIds = new Set(members.map(m => m.user_id));
  const availableUsers = allUsers.filter(u =>
    !memberUserIds.has(u.id) &&
    (u.full_name?.toLowerCase().includes(addSearchQuery.toLowerCase()) ||
     u.email?.toLowerCase().includes(addSearchQuery.toLowerCase()))
  );

  const handleAddMember = async (userId) => {
    try {
      await teamsService.addTeamMember(team.id, userId, selectedRole);
      toast.success(t("members.added"));
      await fetchMembers();
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      setAddSearchQuery("");
    } catch (err) {
      toast.error(err.message || t("members.add_failed"));
    }
  };

  const handleRemoveMember = async (userId) => {
    try {
      await teamsService.removeTeamMember(team.id, userId);
      toast.success(t("members.removed"));
      await fetchMembers();
      queryClient.invalidateQueries({ queryKey: ["teams"] });
    } catch (err) {
      toast.error(err.message || t("members.remove_failed"));
    }
  };

  const filteredMembers = members.filter((member) => {
    const term = searchQuery.toLowerCase();
    return (
      member.full_name?.toLowerCase().includes(term) ||
      member.email?.toLowerCase().includes(term) ||
      member.role?.toLowerCase().includes(term)
    );
  });

  const getRoleBadgeColor = (role) => {
    switch (role?.toLowerCase()) {
      case "team_admin":
        return "bg-purple-100 text-purple-700";
      case "lead":
        return "bg-blue-100 text-blue-700";
      case "manager":
        return "bg-amber-100 text-amber-700";
      default:
        return "bg-slate-100 text-slate-700";
    }
  };

  const formatRole = (role) => {
    if (!role) return t("roles.member");
    const fallback = role.split("_").map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(" ");
    return t(`roles.${role.toLowerCase()}`, { defaultValue: fallback });
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
      <motion.div
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-4 sm:p-6 border-b border-slate-200 org-gradient rounded-t-2xl flex-shrink-0">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-bold text-white truncate">{team.name}</h2>
              <p className="text-white/80 text-xs sm:text-sm">{t("members_count", { count: members.length })}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button
                onClick={() => setShowAddMember(!showAddMember)}
                className="text-white/80 hover:text-white p-2 rounded-full hover:bg-white/20 transition-colors"
                title={t("members.add_member")}
              >
                <Plus size={22} />
              </button>
            )}
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white p-2 rounded-full hover:bg-white/20 transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Add Member Panel */}
        <AnimatePresence>
          {showAddMember && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden border-b border-slate-200 flex-shrink-0"
            >
              <div className="p-4 bg-indigo-50 space-y-3">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder={t("members.search_users_placeholder")}
                      value={addSearchQuery}
                      onChange={(e) => setAddSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-full focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      autoFocus
                    />
                  </div>
                  <select
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    className="text-sm border border-slate-200 rounded-full px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="member">{t("roles.member")}</option>
                    <option value="team_admin">{t("roles.team_admin")}</option>
                  </select>
                </div>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {addSearchQuery.length < 2 ? (
                    availableUsers.length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-2">{t("members.all_added")}</p>
                    ) : (
                      <>
                        <p className="text-xs text-slate-400 font-medium px-1 pb-1">{t("members.suggested")}</p>
                        {availableUsers.slice(0, 6).map((u) => (
                          <div key={u.id} className="flex items-center justify-between p-2 bg-white rounded-xl hover:bg-slate-50">
                            <div className="flex items-center gap-2 min-w-0">
                              <UserAvatar
                                src={u.avatar_url}
                                name={u.full_name || u.email}
                                className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                                textClassName="text-xs"
                              />
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-slate-700 truncate">{u.full_name || t("members.unnamed")}</p>
                                <p className="text-xs text-slate-400 truncate">{u.email}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleAddMember(u.id)}
                              className="text-xs px-3 py-1 bg-indigo-500 text-white rounded-full hover:bg-indigo-600 transition-colors flex-shrink-0"
                            >
                              {t("common:actions.add")}
                            </button>
                          </div>
                        ))}
                      </>
                    )
                  ) : availableUsers.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-2">{t("members.no_users_found")}</p>
                  ) : (
                    availableUsers.slice(0, 10).map((u) => (
                      <div key={u.id} className="flex items-center justify-between p-2 bg-white rounded-xl hover:bg-slate-50">
                        <div className="flex items-center gap-2 min-w-0">
                          <UserAvatar
                            src={u.avatar_url}
                            name={u.full_name || u.email}
                            className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                            textClassName="text-xs"
                          />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-700 truncate">{u.full_name || t("members.unnamed")}</p>
                            <p className="text-xs text-slate-400 truncate">{u.email}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleAddMember(u.id)}
                          className="text-xs px-3 py-1 bg-indigo-500 text-white rounded-full hover:bg-indigo-600 transition-colors flex-shrink-0"
                        >
                          {t("common:actions.add")}
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Search */}
        <div className="p-4 border-b border-slate-200 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder={t("members.search_placeholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>
        </div>

        {/* Members List — min-h-0 lets this flex child scroll instead of pushing the modal past max-h */}
        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-purple-600 animate-spin mb-3" />
              <p className="text-slate-500">{t("members.loading")}</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="w-12 h-12 text-red-400 mb-3" />
              <p className="text-red-600 font-medium">{error}</p>
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Users className="w-12 h-12 text-slate-300 mb-3" />
              <p className="text-slate-500">
                {searchQuery ? t("members.no_match") : t("members.empty")}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredMembers.map((member) => (
                <div
                  key={member.user_id}
                  className="flex items-center p-2 sm:p-3 gap-2 sm:gap-0 bg-slate-50 hover:bg-slate-100 rounded-2xl transition-colors group"
                >
                  {/* Avatar */}
                  <UserAvatar
                    src={member.avatar_url}
                    name={member.full_name || member.email}
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover sm:mr-4 flex-shrink-0"
                    textClassName="text-base sm:text-lg"
                  />

                  {/* Member Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-slate-800 text-sm sm:text-base truncate">
                      {member.full_name || t("members.unnamed_user")}
                    </h4>
                    <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-slate-500">
                      <Mail className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                      <span className="truncate">{member.email}</span>
                    </div>
                  </div>

                  {/* Role Badge */}
                  <div className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium flex-shrink-0 ${getRoleBadgeColor(member.role)}`}>
                    {formatRole(member.role)}
                  </div>

                  {/* Remove Button — always visible on mobile (no hover) */}
                  {isAdmin && (
                    <button
                      onClick={() => handleRemoveMember(member.user_id)}
                      className="ml-1 sm:ml-2 p-1.5 text-slate-400 sm:text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors sm:opacity-0 sm:group-hover:opacity-100 flex-shrink-0"
                      title={t("members.remove_member")}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-500">
              {t("members.shown_count", { shown: filteredMembers.length, count: members.length })}
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-slate-200 text-slate-700 rounded-full hover:bg-slate-300 transition-colors font-medium"
            >
              {t("common:actions.close")}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const TeamStats = ({ teams }) => {
  const { t } = useTranslation("teams");
  const [expanded, setExpanded] = useState(true);

  // Calculate total stats
  const totalTeams = teams.length;
  const totalMembers = teams.reduce((sum, team) => sum + (team.member_count || 0), 0);

  return (
    <div className="bg-white rounded-2xl shadow-md overflow-hidden transition-shadow duration-200 hover:shadow-xl">
      {/* Header */}
      <div className="p-4 flex items-center justify-between org-gradient">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-white" />
          <h3 className="font-bold text-white">{t("stats.title")}</h3>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-white p-1 rounded-full hover:bg-white/20"
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {/* Stats Content */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="p-4">
              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-3 rounded-2xl border border-purple-200">
                  <p className="text-3xl font-bold text-purple-600">{totalTeams}</p>
                  <p className="text-xs text-purple-700">{t("stats.teams")}</p>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-3 rounded-2xl border border-blue-200">
                  <p className="text-3xl font-bold text-blue-600">{totalMembers}</p>
                  <p className="text-xs text-blue-700">{t("stats.members")}</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const TopTeams = ({ teams }) => {
  const { t } = useTranslation("teams");
  const [expanded, setExpanded] = useState(true);

  // Sort teams by member count
  const sortedTeams = [...teams].sort((a, b) => (b.member_count || 0) - (a.member_count || 0)).slice(0, 5);

  return (
    <div className="bg-white rounded-2xl shadow-md overflow-hidden transition-shadow duration-200 hover:shadow-xl">
      {/* Header */}
      <div className="p-4 flex items-center justify-between org-gradient">
        <div className="flex items-center gap-2">
          <Award className="w-5 h-5 text-yellow-300" />
          <h3 className="font-bold text-white">{t("largest.title")}</h3>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-white p-1 rounded-full hover:bg-white/20"
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {/* Top Teams Content */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="p-4">
              {sortedTeams.length > 0 ? (
                <div className="space-y-3">
                  {sortedTeams.map((team, index) => {
                    const teamColor = getTeamColor(team.name);
                    return (
                      <div key={team.id} className="flex items-center p-2 hover:bg-slate-50 rounded-2xl transition-colors">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-slate-100 to-slate-200 flex items-center justify-center text-sm font-bold mr-3 shadow-sm border border-slate-300">
                          {index + 1}
                        </div>
                        <div className={`w-10 h-10 rounded-2xl flex items-center justify-center text-white mr-3 shadow-sm bg-gradient-to-br ${teamColor.from} ${teamColor.to}`}>
                          <Users size={18} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 truncate">{team.name}</p>
                          <p className="text-xs text-slate-500">{t("members_count", { count: team.member_count || 0 })}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-slate-500">{t("largest.empty")}</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TeamsPage;
